import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { clearAccountLinkingCode, clearAccountLinkingSkipped } from "domains/auth/accountLinking";
import { sessionExists } from "domains/auth/initializeAuth";
import Session from "supertokens-web-js/recipe/session";
import { getReturnTo, navigateToReturnTo } from "./helpers";
import { resetNativeHandoff } from "./nativeHandoff";

export const AUTH_QUERY_KEY = ["authCheck"];

export function useSession() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const hasSession = useSuspenseQuery(
    queryOptions({
      queryKey: AUTH_QUERY_KEY,
      queryFn: () => sessionExists(),
      staleTime: Number.POSITIVE_INFINITY,
      retry: 2,
    }),
  );

  return {
    hasSession: hasSession.data,
    onLogin: async () => {
      // Not in the preAPIHook: a mistyped OTP must be able to retry with the code still stashed.
      clearAccountLinkingCode();
      clearAccountLinkingSkipped();
      await hasSession.refetch();
      // A fresh login is a new session for native: re-arm the handoff, which signedUpMiddleware fires on reaching the shell.
      resetNativeHandoff();
      await navigateToReturnTo(navigate, getReturnTo());
    },
    signOut: async () => {
      await queryClient.cancelQueries();
      await Session.signOut();
      queryClient.setQueryData(AUTH_QUERY_KEY, false);
      setTimeout(() => queryClient.clear(), 0);
      // beforeLoad won't rerun without a navigation, so leave the authed shell explicitly.
      await navigate({ href: "/auth", replace: true });
    },
  };
}
